import type { NotificationFullSizeDto, NotificationPageDto } from '@therame/shared';

/**
 * Shape of a TanStack `useInfiniteQuery` cache entry for the notification inbox:
 * one page per fetched batch, each page a cursor page of notifications.
 */
export interface InfiniteNotificationData {
  pages: NotificationPageDto[];
  pageParams: unknown[];
}

const patchPage = (
  page: NotificationFullSizeDto[],
  id: string,
  changes: Partial<NotificationFullSizeDto>,
): NotificationFullSizeDto[] => {
  let changed = false;
  const next = page.map((notification): NotificationFullSizeDto => {
    if (`${notification.id}` !== id) return notification;
    changed = true;
    return { ...notification, ...changes } as NotificationFullSizeDto;
  });
  return changed ? next : page;
};

/**
 * Applies a partial update to the notification with `id` wherever it appears in a
 * TanStack Query cache entry, immutably: unaffected pages/entries keep their
 * reference (so the query only re-renders what changed), and the input is never
 * mutated. Absent ids and non-notification cache shapes (e.g. the unread-count
 * number) are returned untouched, so this is safe to pass to `setQueriesData`
 * across every `['notifications', …]` query at once.
 *
 * Cache entries are plain JSON (from the axios response), not class instances,
 * so a shallow spread is a faithful clone.
 */
export const patchNotificationInCache = <T>(
  data: T,
  id: string,
  changes: Partial<NotificationFullSizeDto>,
): T => {
  if (Array.isArray(data)) {
    return patchPage(data as NotificationFullSizeDto[], id, changes) as unknown as T;
  }

  if (data && typeof data === 'object' && Array.isArray((data as { pages?: unknown }).pages)) {
    const infinite = data as unknown as InfiniteNotificationData;
    let changed = false;
    const pages = infinite.pages.map((page) => {
      const nextItems = patchPage(page.items, id, changes);
      if (nextItems === page.items) return page;
      changed = true;
      return { ...page, items: nextItems };
    });
    return (changed ? { ...infinite, pages } : infinite) as unknown as T;
  }

  return data;
};
